import React, {
    useCallback, useEffect, useMemo, useContext
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { readViewModelValue } from '@xengage/gw-jutro-adapters-react';
import { useValidation } from '@xengage/gw-portals-validation-react'
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import classNames from 'classnames';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { withRouter } from 'react-router-dom';
import { TranslatorContext } from '@jutro/locale';
import { Icon } from '@jutro/components';
import { Link } from '@jutro/router';
import { InputField, InputNumberField, Button } from '@jutro/legacy/components';
import styles from './DinghyMotorComponent.module.scss';
import metadata from './DinghyMotorComponent.metadata.json5';
import messages from './DinghyMotorComponent.messages';
import { accessibilityAddTableRow, focusNextElement, screenReaderAnnounce } from 'premier-capability-hooks';

function DinghyMotorComponent(props) {

    const {
        data: DinghyMotorVM,
        updateWizardData,
        submissionVM,
        DinghyMotorPath,
        boatInformationPath,
        id: dinghyMotorId
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);

    const addDinghyMotor = useCallback(
        (event) => {
            if (event) {
                event.preventDefault();
            }

            DinghyMotorVM.value = DinghyMotorVM.value || []

            const { _xCenter, _dtoName } = DinghyMotorVM;

            const DinghyMotorObjVM = viewModelService.create(
                {},
                _xCenter,
                _dtoName
            );

            DinghyMotorObjVM.isValidationReq = true;
            DinghyMotorObjVM.yeaFie = (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;

            DinghyMotorVM.pushElement(
                DinghyMotorObjVM
            );
            updateWizardData(submissionVM);
            
            accessibilityAddTableRow('Dinghy Motor');
            setTimeout(() => {
                focusNextElement(true, 0, `${dinghyMotorId}yeaFie`);
            }, 3000);   
        },
        [DinghyMotorVM, viewModelService, submissionVM, boatInformationPath, updateWizardData]
    );

    const handleValueChange = useCallback(
        () => {
            updateWizardData(submissionVM);
        },
        [submissionVM, updateWizardData]
    );

    const removeAssignmentItem = useCallback(
        (event, index) => {
            if (event) {
                event.preventDefault();
            }

            const removedDinghyMotor = DinghyMotorVM.value[index];

            DinghyMotorVM.value.splice(index, 1);
            _.set(submissionVM.value, DinghyMotorPath, DinghyMotorVM.value);
            updateWizardData(submissionVM);
            screenReaderAnnounce(translator(messages.removeDinghyMotor, {brandDesc: removedDinghyMotor.braFie}))
            focusNextElement(true, 0, `addDinghyMotorButtonId`); 
        }, [submissionVM, updateWizardData, DinghyMotorPath, DinghyMotorVM?.value]
    );

    const getTableCell = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-shadow
        (item, index, props) => {
            const { path } = props;

            if (path === 'numFie') {
                const tempDinghyMotorPath = 'lobData.recMarLine.coverables.boats[0].additionalParts.dinghyMotorTable'
                const auxiliaryArray = _.get(submissionVM.value, tempDinghyMotorPath);
                const newNumber = auxiliaryArray?.length >= 2 ? auxiliaryArray[auxiliaryArray.length - 2].numFie + 1 : index + 1;

                if (auxiliaryArray[index].numFie === undefined) {
                    auxiliaryArray[index].numFie = newNumber;
                    _.set(submissionVM.value, tempDinghyMotorPath, DinghyMotorVM.value);
                }

                const value = item.numFie ? item.numFie : newNumber;

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        value={value}
                        readOnly
                    />
                );
            }

            if (path === 'yeaFie') {
                const value = item.yeaFie ? item.yeaFie : (_.get(submissionVM, boatInformationPath))?.value?.yeaFie;

                return (
                    <InputField
                        id={index + dinghyMotorId+ path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.yeaFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);
                        }}
                        value={value}
                        maxLength="4"
                    />
                );
            }

            if (path === 'braFie') {
                const value = item.braFie ? item.braFie : '';

                return (
                    <InputField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="String"
                        onValueChange={(val, changedPath) => {
                            item.braFie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'hpfie') {
                const value = item.hpfie ? item.hpfie : '';

                return (
                    <InputNumberField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            item.hpfie = val;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'serNumFie') {
                const value = item.serNumFie ? item.serNumFie : '';

                return (
                    <InputNumberField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.serNumFie = filteredVal;
                            handleValueChange(val, changedPath, index);
                        }}
                        value={value}
                    />
                );
            }

            if (path === 'valFie') {
                const value = item.valFie ? item.valFie : '';

                return (
                    <InputNumberField
                        id={index + path}
                        className={styles.tableColumn}
                        path={path}
                        dataType="number"
                        onValueChange={(val, changedPath) => {
                            const filteredVal = String(val).replace(/[^0-9]/g, ''); // keep only digits

                            item.valFie = filteredVal;
                            handleValueChange(filteredVal, changedPath, index);                            
                        }}
                        value={value}
                    />
                );
            }

        }, [submissionVM.value, DinghyMotorVM.value, handleValueChange])

    const removeAssignmentTableRow = useCallback(
        (item, index) => (
            // eslint-disable-next-line jsx-a11y/anchor-is-valid
            <Link
                onClick={(e) => removeAssignmentItem(e, index, item)}
                className={classNames(styles.deleteSection, styles.deleteRow)}                              
            >
                <Icon icon="mi-delete" className={styles.deleteIcon} />
            </Link>
        ),
        [removeAssignmentItem, translator]
    );

    const renderAddButton = useCallback(() => (
              <Button type='button' id='addDinghyMotorButtonId' aria-label ={translator(messages.addDinghyMotor)} onClick={(e) => addDinghyMotor(e)}>
                        {translator(messages.add)}
                    </Button>
            ), [addDinghyMotor, translator]);

    const overrideProps = useMemo(() => ({
        '@field': {
            showOptional: false,
            showRequired: true
        },
        [`DinghyMotorTable`]: {
            data: DinghyMotorVM.value,
        },
        'addDinghyMotorbtnDiv': {
            content: renderAddButton()
        }

    }), [DinghyMotorVM.value, renderAddButton])

    const resolvers = {
        resolveCallbackMap: {
            addDinghyMotor,
            getTableCell,
            removeAssignmentTableRow
        }
    };

    const readValue = useCallback(
        (fieldId, fieldPath) => readViewModelValue(
            metadata.pageContent,
            DinghyMotorVM,
            fieldId,
            fieldPath,
            overrideProps
        ),
        [DinghyMotorVM, overrideProps]
    );

    return (
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={DinghyMotorVM}
            overrideProps={overrideProps}
            resolveValue={readValue}
            onValueChange={handleValueChange}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );


}

DinghyMotorComponent.propTypes = wizardProps;
export default withRouter(DinghyMotorComponent);
